// The entity layer of a search (docs/entity-layer.md): the things a search names in the tenant's own data models, shown
// as a strip above the answer and opened in a window that lays one entity out field by field, system by system.
// The window is `?entity=<model id>:<key>` (lib/urlmodal.ts: opening it is a push, so Back closes it); stepping to the
// previous or next entity of the same search replaces the entry, like any toggle within a view.
import type { LensCell, LensEntity } from './api.ts';

export const ENTITY_KEY = 'entity';

export interface EntityRef { model: string; key: string }

export function readEntity(p: URLSearchParams): EntityRef | undefined {
  const v = p.get(ENTITY_KEY);
  if (!v) return undefined;
  const at = v.indexOf(':');
  if (at <= 0 || at === v.length - 1) return undefined;
  return { model: v.slice(0, at), key: v.slice(at + 1) };
}

export const encodeEntity = (r: EntityRef): string => `${r.model}:${r.key}`;

export function writeEntity(p: URLSearchParams, v: string | undefined): URLSearchParams {
  const next = new URLSearchParams(p);
  if (v) next.set(ENTITY_KEY, v);
  else for (const k of [ENTITY_KEY, 'esys', 'erows']) next.delete(k);
  return next;
}

/** The same key in another format is the same entity (2754101-6 = FI27541016 is the cloud's business; here the text). */
export const sameKey = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

export interface MatrixCell { value: string | null; differs: boolean; at: number | null }
export interface MatrixRow { label: string; state: LensCell['state'] | 'empty'; cells: Record<string, MatrixCell> }

/**
 * One entity as a table: a row per field of the model (in its order), a column per system that has the entity. A system
 * without a value for the field is empty; `differs` marks a value the other systems do not share.
 */
export function entityMatrix(entity: Pick<LensEntity, 'systems' | 'cells'>, labels: readonly string[]): { systems: string[]; rows: MatrixRow[]; updated: Record<string, number> } {
  const systems = [...entity.systems];
  const updated: Record<string, number> = {};
  const rows = labels.map((label) => {
    const cell = entity.cells[label];
    const cells: Record<string, MatrixCell> = {};
    for (const s of systems) {
      const v = cell?.systems.find((x) => x.system === s);
      cells[s] = v ? { value: v.value, differs: cell!.state === 'conflict' && !v.same, at: v.at } : { value: null, differs: false, at: null };
      if (v) updated[s] = Math.max(updated[s] ?? 0, v.at);
    }
    return { label, state: cell ? cell.state : 'empty' as const, cells };
  });
  return { systems, rows, updated };
}

/** "3 systems · 41 documents · 2 differences" — what the strip says of an entity. */
export function entitySummary(e: Pick<LensEntity, 'systems' | 'records' | 'conflicts'>): string[] {
  const parts = [`${e.systems.length} ${e.systems.length === 1 ? 'system' : 'systems'}`, `${e.records} ${e.records === 1 ? 'document' : 'documents'}`];
  if (e.conflicts > 0) parts.push(`${e.conflicts} ${e.conflicts === 1 ? 'difference' : 'differences'}`);
  return parts;
}

/** The plural a strip uses for many entities of a model: "Customer" → "customers" (a plain English -s, -y → -ies). */
export function pluralModel(name: string): string {
  const n = name.trim().toLowerCase();
  if (/[^aeiou]y$/.test(n)) return n.slice(0, -1) + 'ies';
  if (/(s|x|ch|sh)$/.test(n)) return n + 'es';
  return n + 's';
}

// ---- one value column: the systems that say the same are one value, a difference is a row of its own

export type RowStatus = 'same' | 'differs' | 'single' | 'empty';
export interface ValueGroup { value: string; systems: string[]; at: number }
export interface EntityRow { label: string; status: RowStatus; groups: ValueGroup[]; missing: string[] }

/** Values that are the same in any case and spacing; the cloud's own agreement (`same`, any format) counts first. */
const plain = (v: string) => v.trim().toLowerCase().replace(/[\s.,\-_/()]+/g, '');

/**
 * A field of an entity over the chosen systems: its values grouped (the systems that say the same are one value, the
 * value most of them have first), the chosen systems that have the entity but not the field, and what the row is —
 * the same everywhere it is, different, in one system only, or nowhere.
 */
export function entityRows(entity: Pick<LensEntity, 'systems' | 'cells'>, labels: readonly string[], chosen?: readonly string[]): EntityRow[] {
  const systems = chosen ? entity.systems.filter((s) => chosen.includes(s)) : [...entity.systems];
  return labels.map((label) => {
    const cell = entity.cells[label];
    const groups = new Map<string, ValueGroup>();
    const missing: string[] = [];
    for (const s of systems) {
      const v = cell?.systems.find((x) => x.system === s);
      if (!v) { missing.push(s); continue; }
      const k = v.same && cell!.state !== 'single' ? '\u0000same' : plain(v.value);
      const g = groups.get(k) ?? { value: v.value, systems: [], at: v.at };
      g.systems.push(s);
      if (v.at > g.at) g.at = v.at;
      groups.set(k, g);
    }
    const list = [...groups.values()].sort((a, b) => b.systems.length - a.systems.length || b.at - a.at);
    const status: RowStatus = list.length === 0 ? 'empty' : list.length > 1 ? 'differs' : list[0].systems.length > 1 ? 'same' : 'single';
    return { label, status, groups: list, missing };
  });
}

export type RowFilter = 'all' | 'differs' | 'missing' | 'same';
export const ROW_FILTERS: readonly RowFilter[] = ['all', 'differs', 'missing', 'same'];

export function rowMatches(r: EntityRow, f: RowFilter): boolean {
  if (f === 'differs') return r.status === 'differs';
  if (f === 'missing') return r.missing.length > 0;
  if (f === 'same') return r.status === 'same' && r.missing.length === 0;
  return true;
}

/** The window's own view: which systems' values it shows (`esys=`, all when absent) and which rows (`erows=`). */
export function readEntityView(p: URLSearchParams): { systems?: string[]; rows: RowFilter } {
  const sys = p.get('esys');
  const rows = p.get('erows') as RowFilter | null;
  return { systems: sys ? sys.split(',').filter(Boolean) : undefined, rows: rows && ROW_FILTERS.includes(rows) ? rows : 'all' };
}

export function writeEntityView(p: URLSearchParams, v: { systems?: string[] | null; rows?: RowFilter }): URLSearchParams {
  const next = new URLSearchParams(p);
  if (v.systems !== undefined) { if (v.systems === null) next.delete('esys'); else next.set('esys', v.systems.join(',')); }
  if (v.rows !== undefined) { if (v.rows === 'all') next.delete('erows'); else next.set('erows', v.rows); }
  return next;
}
